import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../config';
import InspectionDetailsModal from './InspectionDetailsModal';

interface Inspection {
  id: string;
  vehicle_registration?: string;
  driver_name?: string;
  start_time: string;
  end_time?: string;
  duration?: number;
  status: string;
  defects_found: number;
  notes?: string;
  mileage?: string;
  fuel_level?: string;
  vehicle_height?: string;
  inspection_type?: string;
}

const Inspections: React.FC = () => {
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedInspection, setSelectedInspection] = useState<Inspection | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  useEffect(() => {
    fetchInspections();
  }, []);

  const fetchInspections = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/inspections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInspections(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching inspections:', error);
      setLoading(false);
    }
  };

  const markAsAbandoned = async (inspectionId: string) => {
    if (!window.confirm('Are you sure you want to mark this inspection as abandoned?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/inspections/mark-abandoned`, {
        inspection_id: inspectionId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchInspections();
      alert('Inspection marked as abandoned');
    } catch (error) {
      console.error('Error marking inspection as abandoned:', error);
      alert('Failed to mark inspection as abandoned');
    }
  };

  const filteredInspections = inspections.filter(inspection => {
    const matchesStatus = filterStatus === 'all' || inspection.status === filterStatus;
    const matchesSearch = !searchTerm || 
      inspection.vehicle_registration?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inspection.driver_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Date filtering
    let matchesDate = true;
    const inspectionDate = new Date(inspection.start_time);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (dateFilter === 'today') {
      const todayStart = new Date(today);
      const todayEnd = new Date(today);
      todayEnd.setHours(23, 59, 59, 999);
      matchesDate = inspectionDate >= todayStart && inspectionDate <= todayEnd;
    } else if (dateFilter === 'yesterday') {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayEnd = new Date(yesterday);
      yesterdayEnd.setHours(23, 59, 59, 999);
      matchesDate = inspectionDate >= yesterday && inspectionDate <= yesterdayEnd;
    } else if (dateFilter === 'week') {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      matchesDate = inspectionDate >= weekAgo;
    } else if (dateFilter === 'month') {
      const monthAgo = new Date(today);
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      matchesDate = inspectionDate >= monthAgo;
    } else if (dateFilter === 'custom' && customStartDate && customEndDate) {
      const startDate = new Date(customStartDate);
      const endDate = new Date(customEndDate);
      endDate.setHours(23, 59, 59, 999);
      matchesDate = inspectionDate >= startDate && inspectionDate <= endDate;
    }
    
    return matchesStatus && matchesSearch && matchesDate;
  });

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">Inspections</h1>
          <button
            onClick={fetchInspections}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
        
        {/* Filters Row */}
        <div className="flex gap-4 flex-wrap">
          <input
            type="text"
            placeholder="Search vehicle or driver..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="incomplete">Incomplete</option>
            <option value="in_progress">In Progress</option>
            <option value="failed">Failed</option>
            <option value="abandoned">Abandoned</option>
          </select>
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="custom">Custom Range</option>
          </select>
          
          {/* Custom Date Range */}
          {dateFilter === 'custom' && (
            <>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="px-4 py-2 border rounded-lg"
                placeholder="Start Date"
              />
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="px-4 py-2 border rounded-lg"
                placeholder="End Date"
              />
            </>
          )}
        </div>
        
        {/* Results Count */}
        <div className="mt-4 text-sm text-gray-600">
          Showing {filteredInspections.length} of {inspections.length} inspections
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Time</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Defects</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredInspections.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                  No inspections found
                </td>
              </tr>
            ) : (
              filteredInspections.map((inspection) => (
                <tr key={inspection.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium">
                    {inspection.vehicle_registration || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {inspection.driver_name || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(inspection.start_time).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {formatDuration(inspection.duration)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded ${
                      inspection.status === 'completed' ? 'bg-green-100 text-green-800' :
                      inspection.status === 'incomplete' ? 'bg-orange-100 text-orange-800' :
                      inspection.status === 'failed' ? 'bg-red-100 text-red-800' :
                      inspection.status === 'abandoned' ? 'bg-gray-100 text-gray-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {inspection.status === 'incomplete' ? 'Incomplete' : inspection.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`font-medium ${
                      inspection.defects_found > 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {inspection.defects_found}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedInspection(inspection)}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View Details
                      </button>
                      {inspection.status === 'in_progress' && (
                        <button
                          onClick={() => markAsAbandoned(inspection.id)}
                          className="text-red-600 hover:text-red-800 font-medium"
                        >
                          Mark Abandoned
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Detailed View Modal */}
      {selectedInspection && (
        <InspectionDetailsModal inspection={selectedInspection} onClose={() => setSelectedInspection(null)} />
      )}

    </div>
  );
};

export default Inspections;
